import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, effect, inject, input, output, signal } from '@angular/core';
import { EAbaConsultorioPrivado } from '@/features/servicos/shared/enums/abas-servicos.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';

import type { ConsultorioPrivadoDTO } from '@/core/api/sicom-api/servicos/pessoa-fisica/consultorio-privado/consultorio-privado.model';
import { ConsultorioPrivadoServicosService } from '@/features/servicos/pessoa-fisica/content/consultorio-privado-servicos/consultorio-privado-servicos.service';
import { formatarDataMascara, montarPatchFormulario, montarRequestProtocolo, simNaoBoolean, type ProtocoloResumoConsulta, valorTexto } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface VisibilidadeAbasConsultorio {
    mostrarCompartilhamento: boolean;
    mostrarEspecialidades: boolean;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-consultorio-privado-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, CardContent, InputText, InputMask, Select, TableModule, DialogModule, VisualizadorPdf],
    templateUrl: './consultorio-privado-servicos.html',
    styleUrl: './consultorio-privado-servicos.scss'
})
export class ConsultorioPrivadoServicos implements OnInit {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;

    protected readonly EAbaConsultorioPrivado = EAbaConsultorioPrivado;
    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');
    readonly abaSelecionadaExterna = input<EAbaConsultorioPrivado | null>(null);
    readonly visibilidadeAbasAlterada = output<VisibilidadeAbasConsultorio>();

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';
    private dadosResposta: ConsultorioPrivadoDTO | null = null;

    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo.pdf');

    readonly abaSelecionada = signal<EAbaConsultorioPrivado>(EAbaConsultorioPrivado.DADOS);
    readonly mostrarAbaCompartilhamento = signal(true);
    readonly mostrarAbaEspecialidades = signal(true);

    yesNoOptions = [
        { label: 'Sim', value: true },
        { label: 'Nao', value: false }
    ];

    usoOptions = [
        { label: 'Exclusivo', value: 'EXCLUSIVO' },
        { label: 'Compartilhado', value: 'COMPARTILHADO' }
    ];

    tipoLicencaOptions = [
        { label: 'Municipal', value: 'MUNICIPAL' },
        { label: 'Estadual', value: 'ESTADUAL' }
    ];

    telefoneTipoOptions = [
        { label: 'FIXO', value: 'F' },
        { label: 'CELULAR', value: 'C' }
    ];

    telefones = [{ tipo: 'C', ddd: '', telefone: '', ramal: '', observacao: '' }];
    compartilhamentos = [{ uf: '', nome: '', entrada: '', saida: '' }];
    especialidades = [{ especialidade: '', areaAtuacao: '', dataInicio: '', dataFim: '' }];
    horariosFuncionamento = [{ diaSemana: '', manhaInicio: '', manhaFim: '', tardeInicio: '', tardeFim: '' }];

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        dataRegistro: [''],
        numeroCnes: [''],
        uso: [''],
        especialidade: this.fb.control<string | boolean | null>(null),
        registroNumeroLicenca: [''],
        registroTipo: [''],
        registroIsento: this.fb.control<boolean | null>(null),
        registroValidade: [''],
        alvaraNumero: [''],
        alvaraIsento: this.fb.control<boolean | null>(null),
        alvaraValidade: [''],
        enderecoCep: [''],
        enderecoLogradouro: [''],
        enderecoBairro: [''],
        enderecoMunicipio: [''],
        enderecoAtualizado: [true],
        enderecoPermiteDivulgacao: [true]
    });
    private readonly servico = inject(ConsultorioPrivadoServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Sincroniza a aba (input) e recarrega ao mudar protocolo/UF (efeitos correm após ngOnInit criar o form).
        effect(() => {
            this.abaSelecionadaExterna();
            this.sincronizarAbaExterna();
        });
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    getTelefoneTipoLabel(value: string): string {
        const opt = this.telefoneTipoOptions.find((o) => o.value === value);
        return opt?.label ?? value;
    }

    ngOnInit(): void {}

    private sincronizarAbaExterna(): void {
        const aba = this.abaSelecionadaExterna();
        if (!aba) {
            return;
        }

        if (!this.abaPermitida(aba)) {
            this.abaSelecionada.set(EAbaConsultorioPrivado.DADOS);
            return;
        }

        this.abaSelecionada.set(aba);
    }

    possuiAnexoAlvara(): boolean {
        return !!valorTexto(this.dadosResposta?.urlAnexoAlvara).trim();
    }

    possuiAnexoRegistroSanitario(): boolean {
        return !!valorTexto(this.dadosResposta?.urlAnexoRegistroSanitario).trim();
    }

    onVisualizarAnexoAlvara(event: Event): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(this.dadosResposta?.urlAnexoAlvara, 'anexo-alvara.pdf');
    }

    onVisualizarAnexoRegistroSanitario(event: Event): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(this.dadosResposta?.urlAnexoRegistroSanitario, 'anexo-registro-sanitario.pdf');
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dadosResposta = null;
            this.mostrarAbaCompartilhamento.set(true);
            this.mostrarAbaEspecialidades.set(true);
            this.visibilidadeAbasAlterada.emit({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
            this.form.reset({
                especialidade: null,
                alvaraIsento: null,
                enderecoAtualizado: true,
                enderecoPermiteDivulgacao: true
            });
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dadosResposta = response;
                    this.atualizarVisibilidadeAbas(response);
                    this.preencherFormulario(response);
                    this.preencherTabelas(response);
                },
                error: () => {
                    this.dadosResposta = null;
                    this.mostrarAbaCompartilhamento.set(true);
                    this.mostrarAbaEspecialidades.set(true);
                    this.visibilidadeAbasAlterada.emit({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
                    this.form.reset({
                        especialidade: null,
                        alvaraIsento: null,
                        enderecoAtualizado: true,
                        enderecoPermiteDivulgacao: true
                    });
                }
            });
    }

    private atualizarVisibilidadeAbas(response: ConsultorioPrivadoDTO): void {
        this.mostrarAbaCompartilhamento.set(this.deveMostrarAbaCompartilhamento(response.inUso));
        this.mostrarAbaEspecialidades.set(this.deveMostrarAbaEspecialidades(response.inEspecializado));
        this.visibilidadeAbasAlterada.emit({
            mostrarCompartilhamento: this.mostrarAbaCompartilhamento(),
            mostrarEspecialidades: this.mostrarAbaEspecialidades()
        });

        if (!this.abaPermitida(this.abaSelecionada())) {
            this.abaSelecionada.set(EAbaConsultorioPrivado.DADOS);
        }
    }

    private abaPermitida(aba: EAbaConsultorioPrivado): boolean {
        if (aba === EAbaConsultorioPrivado.COMP && !this.mostrarAbaCompartilhamento()) {
            return false;
        }

        if (aba === EAbaConsultorioPrivado.ESP && !this.mostrarAbaEspecialidades()) {
            return false;
        }

        return true;
    }

    private deveMostrarAbaCompartilhamento(uso: unknown): boolean {
        const valorUso = valorTexto(uso).trim().toUpperCase();
        return valorUso !== 'E' && valorUso !== 'EXCLUSIVO';
    }

    private deveMostrarAbaEspecialidades(especializado: unknown): boolean {
        const valorEspecializado = valorTexto(especializado).trim().toUpperCase();
        if (['N', 'NAO', 'NÃO', 'NÃƒO', 'FALSE', '0'].includes(valorEspecializado)) {
            return false;
        }

        return true;
    }

    private preencherFormulario(response: ConsultorioPrivadoDTO): void {
        const patch = montarPatchFormulario(
            this.form,
            response as Record<string, unknown>,
            {
                dataRegistro: 'dtRegistro',
                numeroCnes: 'cnes',
                uso: 'inUso',
                especialidade: 'inEspecializado',
                registroNumeroLicenca: 'registroSanitario',
                registroTipo: 'inTipoRegSanitario',
                registroIsento: 'inIsencaoRegistroSanitario',
                registroValidade: 'dtValidadeRegSanitario',
                alvaraNumero: 'alvara',
                alvaraIsento: 'inIsencaoAlvara',
                alvaraValidade: 'dtValidadeAlvara',
                enderecoCep: 'cep',
                enderecoLogradouro: 'logradouro',
                enderecoBairro: 'bairro',
                enderecoMunicipio: ['municipio.descricao', 'municipio'],
                enderecoPermiteDivulgacao: 'inDivulga'
            },
            {
                dataRegistro: formatarDataMascara,
                registroValidade: formatarDataMascara,
                alvaraValidade: formatarDataMascara,
                especialidade: simNaoBoolean,
                registroIsento: simNaoBoolean,
                alvaraIsento: simNaoBoolean,
                enderecoPermiteDivulgacao: simNaoBoolean
            }
        );

        patch['enderecoAtualizado'] = Boolean(response.dtAtualizacao);
        this.form.patchValue(patch, { emitEvent: false });
    }

    private preencherTabelas(response: ConsultorioPrivadoDTO): void {
        this.telefones =
            response.telefones?.map((telefone) => ({
                tipo: valorTexto(telefone.tipoTelefone),
                ddd: valorTexto(telefone.ddd),
                telefone: valorTexto(telefone.telefone),
                ramal: valorTexto(telefone.ramal),
                observacao: valorTexto(telefone.observacao)
            })) ?? [];

        this.compartilhamentos =
            response.medicos?.map((medico) => ({
                uf: valorTexto(medico.uf),
                nome: valorTexto(medico.nome),
                entrada: formatarDataMascara(medico.dtEntrada),
                saida: formatarDataMascara(medico.dtFim)
            })) ?? [];

        this.especialidades =
            response.especialidades?.map((especialidade) => ({
                especialidade: valorTexto(especialidade.especialidade?.descricao),
                areaAtuacao: valorTexto(especialidade.areaAtuacao?.descricao),
                dataInicio: formatarDataMascara(especialidade.dtInclusao),
                dataFim: formatarDataMascara(especialidade.dtFim)
            })) ?? [];

        this.horariosFuncionamento =
            response.horarios?.map((horario) => ({
                diaSemana: valorTexto(horario.diaSemanaInicio?.descricao || horario.diaSemanaFim?.descricao),
                manhaInicio: valorTexto(horario.horaManhaInicio),
                manhaFim: valorTexto(horario.horaManhaFim),
                tardeInicio: valorTexto(horario.horaTardeInicio),
                tardeFim: valorTexto(horario.horaTardeFim)
            })) ?? [];
    }

    private visualizarAnexoPorCampo(campoAnexo: unknown, nomeArquivo: string): void {
        const idAnexo = this.extrairIdAnexo(campoAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Id do anexo nao identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.nomeArquivoAnexo.set(nomeArquivo);
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const texto = valorTexto(campoAnexo).trim();
        if (!texto) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(texto);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(texto, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e segue fallback.
        }

        if (/^[A-Za-z0-9_-]+$/.test(texto)) {
            return texto;
        }

        return null;
    }
}
